import { fetchChallenge, postChallengeResponse } from "../auth";
import { API_BASE_URL } from "../constants";
const baseApiUrl = API_BASE_URL;
/**
 * Generate wallet using the server endpoint
 */
export const generateWallet = async () => {
    try {
        const response = await fetch(`${ baseApiUrl }/wallets/new-btc`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${ response.status }`);
        }

        const walletData = await response.json();

        return {
            privateKey: walletData.privateKey,
            publicKey: walletData.publicKey,
            address: walletData.address,
        };

    } catch (error) {
        console.error('Error generating wallet:', error);
        throw new Error('Failed to generate wallet. Please try again.');
    }
};

/**
 * Sign a message with a generated wallet private key using server endpoint
 */
export const signMessageWithGenerated = async (message, privateKeyWIF) => {
    try {
        const response = await fetch(`${ baseApiUrl }/wallets/sign-btc-message`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                message,
                privateKey: privateKeyWIF
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${ response.status }`);
        }

        const result = await response.json();
        return result.signature;

    } catch (error) {
        console.error('Error signing message:', error);
        throw new Error('Failed to sign message with generated wallet.');
    }
};

/**
 * Login with a generated wallet
 */
export const loginWithGenerated = async (walletData) => {
    try {
        const { address, privateKey } = walletData;

        // Fetch challenge from server
        const challenge = await fetchChallenge(address);
        console.log("Challenge:", challenge);

        // Sign the challenge
        const signature = await signMessageWithGenerated(challenge, privateKey);
        console.log("Signature:", signature);

        // Submit challenge response
        const response = await postChallengeResponse({
            address,
            message: challenge,
            signature,
        });

        if (!response.token) {
            throw new Error('No auth token received');
        }

        console.log("Login response:", response);

        return {
            address,
            walletType: 'GENERATED',
            token: response.token,
            isConnected: true,
        };
    } catch (error) {
        console.error('Error logging in with generated wallet:', error);
        throw new Error(`Login failed: ${ error.message }`);
    }
};

/**
 * Save wallet data to localStorage with encryption warning
 */
export const saveWalletToLocalStorage = (walletData, walletName = 'Generated Wallet') => {
    try {
        const walletInfo = {
            name: walletName,
            address: walletData.address,
            publicKey: walletData.publicKey,
            privateKey: walletData.privateKey, // Note: This is sensitive data
            walletType: 'GENERATED',
            createdAt: new Date().toISOString(),
            isFavorite: true, // Make it favorite by default
        };

        // Save to localStorage
        const existingWallets = JSON.parse(localStorage.getItem('generatedWallets') || '[]');
        existingWallets.push(walletInfo);
        localStorage.setItem('generatedWallets', JSON.stringify(existingWallets));

        // Also save as main wallet if none exists
        const existingMainWallet = localStorage.getItem('mainWallet');
        if (!existingMainWallet) {
            localStorage.setItem('mainWallet', JSON.stringify(walletInfo));
        }

        return walletInfo;
    } catch (error) {
        console.error('Error saving wallet to localStorage:', error);
        throw new Error('Failed to save wallet');
    }
};

/**
 * Load generated wallets from localStorage
 */
export const loadGeneratedWallets = () => {
    try {
        const wallets = JSON.parse(localStorage.getItem('generatedWallets') || '[]');
        return wallets;
    } catch (error) {
        console.error('Error loading generated wallets:', error);
        return [];
    }
};

/**
 * Delete a generated wallet from localStorage
 */
export const deleteGeneratedWallet = (address) => {
    try {
        const existingWallets = JSON.parse(localStorage.getItem('generatedWallets') || '[]');
        const updatedWallets = existingWallets.filter(wallet => wallet.address !== address);
        localStorage.setItem('generatedWallets', JSON.stringify(updatedWallets));

        // If this was the main wallet, remove it
        const mainWallet = JSON.parse(localStorage.getItem('mainWallet') || 'null');
        if (mainWallet && mainWallet.address === address) {
            localStorage.removeItem('mainWallet');
        }

        return true;
    } catch (error) {
        console.error('Error deleting generated wallet:', error);
        throw new Error('Failed to delete wallet');
    }
};
